/* eslint-disable @typescript-eslint/ban-ts-comment */
/**
 * @file SPSSPurchaseAdvisorSelfAssessment.tsx
 * Exports a top-level React component that handles routing, context, and i18n.
 */
import { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import './SPSSPurchaseAdvisorSelfAssessment.scss';
import 'src/common/style/common.scss';
import {
  urxScriptLoader,
  urxScript,
} from 'src/common/hooks/widgets/globalDefinitions';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import { UT30SPSS } from '../constants/default';
import Welcome from './Templates/Welcome/Welcome';
import Assessment from './Templates/Assessment/Assessment';
import { Landing } from './Molecules/Landing/Landing';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { Modal } from 'carbon-components-react';
import countryList from '../services/currencies.json';
import {
  useLang,
  LangDefaults,
  determineCountry,
  getMessage,
} from 'src/common/hooks/widgets/useLang';
import messages from '../locales/messages';
import { segmentsTracking } from 'src/common/lib/tracker';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import Cookies from 'js-cookie';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import * as URX_FORM from '../constants/urx-form';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { FormModal } from 'src/common/utils/FormModal';
import Thanks from '../components/Molecules/Thanks/Thanks';

/**
 * App component.
 *
 * The top-level parent component that defines this application.
 *
 * @param {object} props Component props.
 * @param {Element} props.element The DOM element that contains this widget.
 */

export interface AppProps {
  element: Element;
  langCode: string;
  test: boolean;
  countryToTest: string;
}

export type que2bValueProps = {
  options: {
    Q3BA1: {
      value: string;
      checked: boolean;
    };
    Q3BA2: {
      value: string;
      checked: boolean;
    };
    Q3BA3: {
      value: string;
      checked: boolean;
    };
    Q3BA4: {
      value: string;
      checked: boolean;
    };
  };
};

const SPSSPurchaseAdvisorSelfAssessment = ({
  langCode,
  test,
  countryToTest,
}: AppProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [openLanding, setOpenLanding] = useState(false);
  const { bpName, companyId, logo, desc, mssp } = useBPName(UT30SPSS, true);
  const [openAssessment, setOpenAssessment] = useState(false);
  const [step, setStep] = useState(1);
  const [tile, setTile] = useState('');
  const [, setBpConsentMail] = useState(false);
  const [statsPkg, setStatsPkg] = useState('');
  const [formComplete, setFormComplete] = useState(false);
  const [, setCurrency] = useState('');
  const [, setCountry] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lang, userLocale] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  const [que1Value, setQue1Value] = useState('');
  const [que2AValue, setQue2AValue] = useState('');
  const [que2BValue, setQue2BValue] = useState<que2bValueProps>({
    options: {
      Q3BA1: {
        value: '',
        checked: false,
      },
      Q3BA2: {
        value: '',
        checked: false,
      },
      Q3BA3: {
        value: '',
        checked: false,
      },
      Q3BA4: {
        value: '',
        checked: false,
      },
    },
  });
  const [model2, setModel2] = useState(false);
  // lang and userlocale is based out of ddo

  /* Have urx form deault in us-en when on language us page */
  const [urxlang, setUrxLang] = useState(
    LangDefaults.localCountryLang as IBMLocale,
  );

  const [localCurrency, setlocalCurrency] = useState<string>(localeCode);
  const [toggleCountry, setToggleCountry] = useState(false);
  const [modalLocalCountry, setModalLocalCountry] = useState('');
  const [modalLocalCurrency, setModalLocalCurrency] = useState('USD');
  const [modalLocalLanguage, setModalLocalLanguage] = useState('English');
  const [localExist, setLocalExist] = useState(false);
  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;
  const [selectedLocale, setSelectedLocale] = useState(localStorageFound);
  const [isRequired, setIsRequired] = useState(false);
  const [cookieContent, setCookieContent] = useState('');
  const cookieURL = useCookiesURL();
  const [pathSelected, setPathSelected] = useState('');
  const [closeModal, setCloseModal] = useState(false);

  // Determine Country
  determineCountry(
    setLocalExist,
    setSelectedLocale,
    setModalLocalCountry,
    setModalLocalLanguage,
    setModalLocalCurrency,
    setToggleCountry,
    lang,
    countryList,
    true,
    test,
    localeCode,
  );

  useEffect(() => {
    // Setting values to cookies
    const que1Info =
      que1Value &&
      ' Question 2: ' +
        ' Features important to you ' +
        ' | Answer2: ' +
        que1Value +
        ' | ';
    const que2AInfo =
      que2AValue &&
      ' Question 3: ' +
        ' Features selected ' +
        ' | Answer3: ' +
        radioEdition +
        ' | ';
    const que2BInfo =
      (que2BValue.options.Q3BA1.value ||
        que2BValue.options.Q3BA2.value ||
        que2BValue.options.Q3BA3.value ||
        que2BValue.options.Q3BA4.value) &&
      ' Question 3: ' +
        ' Features selected ' +
        ' | Answer3: ' +
        checkboxEdition +
        ' | ';
    const spssStatsInfo =
      statsPkg &&
      ' Question 2: ' +
        ' Which SPSS Statistics Package do you currently have? ' +
        ' | Answer2: ' +
        statsPkg +
        ' | ';
    setCookieContent(
      ' | Path Selected: ' +
        pathSelected +
        ' | Question1: ' +
        ' What describes you the best? ' +
        ' | Answer1: ' +
        tile +
        ' | ' +
        que1Info +
        que2AInfo +
        que2BInfo +
        spssStatsInfo +
        (getBp() ? `|Preferred BP:${getBp()}` : ''),
    );
  }, [que1Value, que2AValue, que2BValue, statsPkg, tile, pathSelected]);

  /* Have urx form deault in us-en when on language us page */
  useEffect(() => {
    const fetchedUrxLang = localeCode; // us-en
    setUrxLang(userLocale as IBMLocale); // de-de
    // if de-de === de-de
    if (fetchedUrxLang !== lang) {
      setUrxLang(fetchedUrxLang as IBMLocale); // default us-en
    }
  });

  const getBp = () => {
    if (bpName) return bpName;
    if (logo) return companyId;
    if (desc) return desc;
    if (mssp) return mssp;

    const bpField = document.querySelector('#Q_BPNAME') as HTMLInputElement;
    return bpField ? bpField.value : '';
  };
  useEffect(() => {
    setlocalCurrency(userLocale); // show local Currency
  }, [userLocale, localeCode, localCurrency]);

  const [environment] = useEnv();
  window.urxEnvironment = environment;

  window.onUrxFormSubmitSuccess = () => {
    console.log(`\n***form submited`, drawerOpen);
    setFormComplete(!formComplete);
    setCloseModal(true);
    setOpenAssessment(false);
    setDrawerOpen(false); // Hide Drawer on Thank you page
  };

  const setCookie = () => {
    const mailCheckbox =
      document.querySelector<HTMLInputElement>('#Q_MAIL_CONSENT');
    setBpConsentMail(!!mailCheckbox?.checked); // set false if unchecked or if undefined
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            cookieContent +
            ' SPSS Purchasing Advisor Self Assessment-' +
            ' | FormId:' +
            URX_FORM.FORM_ID,
          Q_ASSET_NAME: 'SPSS Purchasing Advisor Self Assessment', // ISC - Asset Name
        },
      }),
    );
  };

  const handleUrxForm = () => {
    console.log(`Going to open the FORM`);
    setDrawerOpen(true);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'SPSS Purchasing Advisor Self Assessment',
      object: 'SPSS Purchasing Advisor Self Assessment page',
      resultValue: 'CTA clicked',
      CTA: `Email Sales - ${pathSelected}`,
      location: 'Assessment',
      action: '',
      field: '',
    });
    setOpenLanding(false);
    setOpenAssessment(false);
    initUrxForm(
      environment,
      URX_FORM.INSTANCE_ID,
      URX_FORM.FORM_ID,
      localeCode as IBMLocale,
      companyId,
    );
    setCookie();
  };

  const handleLaunchModalLanding = () => {
    setOpenLanding(true);
    setOpenAssessment(false);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'SPSS Purchasing Advisor Self Assessment',
      object: 'SPSS Purchasing Advisor Self Assessment page',
      resultValue: 'CTA clicked',
      CTA: 'Launch Assessment',
      location: `${pathSelected} Assessment`,
      action: '',
      field: '',
    });
  };

  const handleLaunchModal = () => {
    setOpenLanding(false);
    setOpenAssessment(true);
    setStep(1);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'SPSS Purchasing Advisor Self Assessment',
      object: 'SPSS Purchasing Advisor Self Assessment page',
      resultValue: 'CTA clicked',
      CTA: 'Launch Assessment',
      location: `${pathSelected} Assessment`,
      action: '',
      field: '',
    });
  };

  const handleCloseModal = () => {
    setOpenAssessment(false);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'SPSS Purchasing Advisor Self Assessment',
      object: 'SPSS Purchasing Advisor Self Assessment page',
      resultValue: 'CTA clicked',
      CTA: 'Close',
      location: `${pathSelected} Assessment`,
      action: '',
      field: '',
    });
  };

  const handleCloseQuestions = () => {
    setOpenAssessment(false);
    setOpenLanding(true);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'SPSS Purchasing Advisor Self Assessment',
      object: 'SPSS Purchasing Advisor Self Assessment Page',
      resultValue: 'CTA clicked',
      CTA: 'Back on first question',
      location: `${pathSelected} Assessment`,
      action: '',
      field: '',
    });
  };

  const reset = () => {
    setQue1Value('');
    setQue2AValue('');
    setStatsPkg('');
    setQue2BValue({
      options: {
        Q3BA1: {
          value: '',
          checked: false,
        },
        Q3BA2: {
          value: '',
          checked: false,
        },
        Q3BA3: {
          value: '',
          checked: false,
        },
        Q3BA4: {
          value: '',
          checked: false,
        },
      },
    });
    setStep(1);
    segmentsTracking('CTA Clicked', {
      pageTitle: 'SPSS Purchasing Advisor Self Assessment',
      object: 'SPSS Purchasing Advisor Self AssessmentPage',
      resultValue: 'CTA clicked',
      CTA: 'Star Over',
      location: `${pathSelected} Assessment`,
      action: '',
      field: '',
    });
    setOpenLanding(true);
    setOpenAssessment(false);
    setTile('');
  };

  const radioEdition =
    que2AValue === 'A'
      ? 'Base edition'
      : que2AValue === 'B'
      ? 'Standard edition'
      : que2AValue === 'C'
      ? 'Professional edition'
      : 'Premium edition';

  const checkboxEdition =
    que2BValue.options.Q3BA1.value &&
    !que2BValue.options.Q3BA2.value &&
    !que2BValue.options.Q3BA3.value &&
    !que2BValue.options.Q3BA4.value
      ? 'Base Edition'
      : que2BValue.options.Q3BA1.value &&
        que2BValue.options.Q3BA2.value &&
        !que2BValue.options.Q3BA3.value &&
        !que2BValue.options.Q3BA4.value
      ? 'Base Edition and Custom Tables & Advanced Statistics'
      : que2BValue.options.Q3BA1.value &&
        que2BValue.options.Q3BA2.value &&
        que2BValue.options.Q3BA3.value &&
        !que2BValue.options.Q3BA4.value
      ? 'Base Edition, Custom Tables & Advanced Statistics and Complex Sampling & Testing'
      : que2BValue.options.Q3BA1.value &&
        que2BValue.options.Q3BA2.value &&
        que2BValue.options.Q3BA3.value &&
        que2BValue.options.Q3BA4.value
      ? 'Base Edition, Custom Tables & Advanced Statistics, Complex Sampling & Testing and Forecasting & Decision Trees'
      : !que2BValue.options.Q3BA1.value &&
        que2BValue.options.Q3BA2.value &&
        !que2BValue.options.Q3BA3.value &&
        !que2BValue.options.Q3BA4.value
      ? 'Custom Tables & Advanced Statistics'
      : !que2BValue.options.Q3BA1.value &&
        !que2BValue.options.Q3BA2.value &&
        que2BValue.options.Q3BA3.value &&
        !que2BValue.options.Q3BA4.value
      ? 'Complex Sampling & Testing'
      : !que2BValue.options.Q3BA1.value &&
        !que2BValue.options.Q3BA2.value &&
        !que2BValue.options.Q3BA3.value &&
        que2BValue.options.Q3BA4.value
      ? 'Forecasting & Decision Trees'
      : que2BValue.options.Q3BA1.value &&
        !que2BValue.options.Q3BA2.value &&
        que2BValue.options.Q3BA3.value &&
        !que2BValue.options.Q3BA4.value
      ? 'Base Edition and Complex Sampling & Testing'
      : que2BValue.options.Q3BA1.value &&
        !que2BValue.options.Q3BA2.value &&
        !que2BValue.options.Q3BA3.value &&
        que2BValue.options.Q3BA4.value
      ? 'Base Edition and Forecasting & Decision Trees'
      : !que2BValue.options.Q3BA1.value &&
        que2BValue.options.Q3BA2.value &&
        que2BValue.options.Q3BA3.value &&
        !que2BValue.options.Q3BA4.value
      ? 'Custom Tables & Advanced Statistics and Complex Sampling & Testing'
      : !que2BValue.options.Q3BA1.value &&
        que2BValue.options.Q3BA2.value &&
        !que2BValue.options.Q3BA3.value &&
        que2BValue.options.Q3BA4.value
      ? 'Custom Tables & Advanced Statistics and Forecasting & Decision Trees'
      : !que2BValue.options.Q3BA1.value &&
        !que2BValue.options.Q3BA2.value &&
        que2BValue.options.Q3BA3.value &&
        que2BValue.options.Q3BA4.value
      ? 'Complex Sampling & Testing and Forecasting & Decision Trees'
      : !que2BValue.options.Q3BA1.value &&
        que2BValue.options.Q3BA2.value &&
        que2BValue.options.Q3BA3.value &&
        que2BValue.options.Q3BA4.value
      ? 'Custom Tables & Advanced Statistics, Complex Sampling & Testing and Forecasting & Decision Trees'
      : que2BValue.options.Q3BA1.value &&
        que2BValue.options.Q3BA2.value &&
        !que2BValue.options.Q3BA3.value &&
        que2BValue.options.Q3BA4.value
      ? 'Base Edition, Custom Tables & Advanced Statistics and Forecasting & Decision Trees'
      : '';

  return (
    <div className="bx--grid spss_widget widget-styles">
      <Helmet>
        <script type="application/javascript" src={urxScriptLoader}></script>
        <script type="application/javascript" src={urxScript}></script>
      </Helmet>
      {!openAssessment && !openLanding && (
        <Welcome
          handleLaunchModal={handleLaunchModalLanding}
          model2={model2}
          setModel2={setModel2}
          modalBtnLangText={modalLocalLanguage}
          localExist={localExist}
          toggleCountry={toggleCountry}
          flag={false} // to enable/disable globalization
        />
      )}
      <Landing
        openLanding={openLanding}
        setOpenLanding={setOpenLanding}
        handleLaunchModal={handleLaunchModal}
        setTile={setTile}
        tile={tile}
        setPathSelected={setPathSelected}
        setQue1Value={setQue1Value}
        setQue2AValue={setQue2AValue}
        setQue2BValue={setQue2BValue}
        setStatsPkg={setStatsPkg}
      ></Landing>
      <Assessment
        open={openAssessment}
        langCode={langCode}
        bpName={bpName}
        logo={logo}
        companyId={companyId}
        handleCloseModal={handleCloseModal}
        handleCloseQuestions={handleCloseQuestions}
        step={step}
        setStep={setStep}
        que1Value={que1Value}
        setQue1Value={setQue1Value}
        que2AValue={que2AValue}
        setQue2AValue={setQue2AValue}
        que2BValue={que2BValue}
        setQue2BValue={setQue2BValue}
        localeCode={urxlang}
        reset={reset}
        formComplete={formComplete}
        setFormComplete={setFormComplete}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        handleUrxForm={handleUrxForm}
        radioEdition={radioEdition}
        checkboxEdition={checkboxEdition}
        tile={tile}
        statsPkg={statsPkg}
        setStatsPkg={setStatsPkg}
        isRequired={isRequired}
        setIsRequired={setIsRequired}
        pathSelected={pathSelected}
      />

      <div className="bx--row">
        <div
          className="bx--col-lg-4 bx--col-md-8 bx--col-sm-8 mar1 bx--offset-lg-12"
          id="countryModal"
        >
          <Modal
            open={model2}
            size="sm"
            preventCloseOnClickOutside={true}
            onRequestClose={() => {
              setModel2(false);
            }}
            modalLabel={`${getMessage(
              messages.foundCountry,
            )} ${modalLocalCountry}. ${getMessage(
              messages.accessPageCapitalized,
            )} ${modalLocalLanguage} (${modalLocalCurrency})`}
            modalHeading={`${getMessage(
              messages.availableIn,
            )} ${modalLocalLanguage} ${getMessage(
              messages.localCurrency_text,
            )} ${modalLocalCurrency}`}
            secondaryButtonText={getMessage(messages.stayHere)}
            primaryButtonText={getMessage(messages.takeMeThere)}
            onRequestSubmit={() => {
              localStorage.setItem('langLocale', selectedLocale);
              (window.location.href =
                'https://www.ibm.com/' +
                selectedLocale +
                '/products/spss-statistics/pricing'),
                segmentsTracking('CTA Clicked', {
                  pageTitle: 'SPSS Purchasing Advisor Self Assessment',
                  object: 'SPSS Purchasing Advisor Self Assessment Page',
                  resultValue: 'CTA clicked',
                  CTA: 'Switched to country ' + modalLocalCountry,
                  location: `${pathSelected} Assessment`,
                  action: '',
                  field: '',
                });
              return true;
            }}
            onSecondarySubmit={() => {
              segmentsTracking('CTA Clicked', {
                pageTitle: 'SPSS Purchasing Advisor Self Assessment',
                object: 'SPSS Purchasing Advisor Self Assessment Page',
                resultValue: 'CTA clicked',
                CTA: 'Stay here button clicked ' + modalLocalCountry,
                location: `${pathSelected} Assessment`,
                action: '',
                field: '',
              });
              setToggleCountry(false);
              setModel2(false);
              return true;
            }}
          ></Modal>
          <FormModal
            pageTitle="SPSS Purchasing Advisor Self Assessment"
            object="SPSS Purchasing Advisor Self Assessment Page"
            productTitle={'SPSS Purchasing Adviso Self Assessmentr'}
            pageUrl={''}
            location={'Assessment URX Form'}
            bpName={bpName}
            logo={logo}
            drawerOpen={drawerOpen}
            setDrawerOpen={setDrawerOpen}
            closeModal={closeModal}
            setCloseModal={setCloseModal}
          />
          {formComplete && <Thanks bpName={bpName} />}
        </div>
      </div>
    </div>
  );
};

export default SPSSPurchaseAdvisorSelfAssessment;
